import { isQuiet } from './repeat.js';
import type { Card } from './types.js';

/**
 * What the morning check-in can add to its question: the recurring cards returning today and
 * the ongoing ones gone quiet. `wakeDay` gives a card's return date as a local calendar day
 * ("YYYY-MM-DD"), so a server can compute it in the user's zone rather than its own.
 */
export interface Digest {
  woke: Card[];
  quiet: Card[];
}

export function digestOf(cards: Card[], day: string, now: number, wakeDay: (card: Card) => string | null): Digest {
  const open = cards.filter((c) => c.deletedAt === null && c.status === 'open');
  const woke = open.filter((c) => c.repeat && c.repeat.kind !== 'ongoing' && wakeDay(c) === day).sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0));
  const quiet = open.filter((c) => isQuiet(c, now)).sort((a, b) => (a.touchedAt ?? a.createdAt) - (b.touchedAt ?? b.createdAt));
  return { woke, quiet };
}

/** One line per part, titles joined; at most three titles named, the rest counted. */
export function digestLines(d: Digest, text: { back: string; quiet: string; more: (n: number) => string }): string[] {
  const list = (cards: Card[]): string => {
    const names = cards.slice(0, 3).map((c) => c.title);
    return cards.length > 3 ? `${names.join(', ')} ${text.more(cards.length - 3)}` : names.join(', ');
  };
  const lines: string[] = [];
  if (d.woke.length) lines.push(`${text.back} ${list(d.woke)}`);
  if (d.quiet.length) lines.push(`${text.quiet} ${list(d.quiet)}`);
  return lines;
}

/** "YYYY-MM-DD" of a local calendar day shifted by whole days. */
export function shiftDay(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}
